import {
  Button,
  Checkbox,
  Flex,
  FormLabel,
  Heading,
  Tag,
  TagLabel,
  TagLeftIcon,
  Text,
} from "@chakra-ui/react";
import { Form, Formik, FormikHelpers } from "formik";
import { InputControl } from "formik-chakra-ui";
import React, { useCallback, useEffect, useState } from "react";
import { RiLightbulbLine } from "react-icons/ri";
import { ResizableDrawer } from "src/components/Layout/ResizableDrawer";
import {
  createOfferConfiguration,
  OfferConfigurationValidationResult,
  updateOfferConfiguration,
} from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import {
  generateKey,
  isUserGeneratedKey,
} from "src/scenes/orgAdmin/formTemplates/ContentTab/V2/autoGenerateKey";
import { validateWithZod } from "src/services/formValidations";
import { PASSWORD_MANAGER_IGNORE } from "src/types/passwordManager";
import { z } from "zod";
import { ClearLowerRankedConfiguration } from "./offerConfigurationTypes";

export interface ClearLowerRankedFormData {
  name: string;
  description: string;
  key: string;
  userGeneratedKey: boolean;
  remove_lower_ranked_waitlists: boolean;
  revoke_lower_ranked_offers: boolean;
  form_template_id: string;
}

const defaultValues: ClearLowerRankedFormData = {
  name: "Clear lower ranked waitlists and offers",
  description:
    "When an offer is accepted, automatically clear lower ranked waitlists and offers",
  key: "",
  userGeneratedKey: false,
  remove_lower_ranked_waitlists: true,
  revoke_lower_ranked_offers: true,
  form_template_id: "",
};

const FormSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string(),
  key: z.string().min(1, "A rule key is required"),
  userGeneratedKey: z.boolean(),
  remove_lower_ranked_waitlists: z.boolean(),
  revoke_lower_ranked_offers: z.boolean(),
  form_template_id: z.string(),
});

export interface Props {
  configuration: ClearLowerRankedConfiguration | null;
  formTemplateId: string;
  isOpen: boolean;
  onClose: () => void;
  refetch: () => Promise<void>;
}

const componentId = "clear-lower-ranked-form";

const toFormData = (
  config: ClearLowerRankedConfiguration
): ClearLowerRankedFormData => {
  const { configuration } = config;
  const existingKey = config.key ?? "";
  const ruleKey =
    existingKey.trim().length > 0 ? existingKey : generateKey(config.name);

  return {
    name: config.name,
    description: config.description || "",
    key: ruleKey,
    userGeneratedKey: isUserGeneratedKey(ruleKey, config.name),
    remove_lower_ranked_waitlists: configuration.remove_lower_ranked_waitlists,
    revoke_lower_ranked_offers: configuration.revoke_lower_ranked_offers,
    form_template_id: config.form_template_id,
  };
};

export const ClearLowerRankedDrawer: React.FC<Props> = ({
  configuration,
  formTemplateId,
  isOpen,
  onClose,
  refetch,
}) => {
  const toast = useAvelaToast();
  const [validationResults, setValidationResults] = useState<
    OfferConfigurationValidationResult[]
  >([]);

  const isEditMode = !!configuration;

  const keyError = validationResults.find(
    (result) =>
      result.level === "Configuration" &&
      "fieldName" in result &&
      result.fieldName === "key"
  )?.error;

  const generalErrors = validationResults
    .filter((result) => result.level !== "School")
    .filter((result) => !("fieldName" in result && result.fieldName === "key"))
    .map((result) => result.error);

  useEffect(() => {
    if (isOpen) {
      setValidationResults([]);
    }
  }, [isOpen, configuration?.id]);

  const handleClose = useCallback(() => {
    setValidationResults([]);
    onClose();
  }, [onClose]);

  const submitForm = useCallback(
    async (
      values: ClearLowerRankedFormData,
      formikHelpers: FormikHelpers<ClearLowerRankedFormData>
    ) => {
      try {
        setValidationResults([]);

        const configurationPayload = {
          remove_lower_ranked_waitlists: values.remove_lower_ranked_waitlists,
          revoke_lower_ranked_offers: values.revoke_lower_ranked_offers,
        };

        const response = configuration
          ? await updateOfferConfiguration({
              path: { id: configuration.id },
              body: {
                name: values.name,
                description: values.description || undefined,
                key: values.key,
                configuration: configurationPayload,
              },
            })
          : await createOfferConfiguration({
              body: {
                name: values.name,
                description: values.description || undefined,
                key: values.key,
                form_template_id: values.form_template_id,
                active: false,
                configuration: configurationPayload,
                configuration_type: "CLEAR_LOWER_RANKED",
              },
            });

        const outcome = response.data?.data;
        if (outcome?.result === "invalid") {
          setValidationResults(outcome.validationResults);
          return;
        }

        toast({
          title: "Success",
          description: isEditMode ? "Rule updated" : "Rule created",
          status: "success",
        });

        await refetch();
        handleClose();
      } catch (error) {
        console.error(error);
        toast.error({
          title: `Failed to ${isEditMode ? "update" : "create"} rule`,
          description: `An error occurred while ${
            isEditMode ? "updating" : "creating"
          } the rule.`,
        });
      } finally {
        formikHelpers.setSubmitting(false);
      }
    },
    [configuration, isEditMode, toast, refetch, handleClose]
  );

  const headerContent = (
    <Heading as="h3" size="1.25rem">
      {isEditMode ? "Edit rule" : "Create rule"}
    </Heading>
  );

  return (
    <Formik<ClearLowerRankedFormData>
      key={configuration?.id ?? `new-${isOpen}`}
      initialValues={
        configuration
          ? toFormData(configuration)
          : {
              ...defaultValues,
              form_template_id: formTemplateId,
              key: generateKey(defaultValues.name),
            }
      }
      validate={validateWithZod(FormSchema)}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={async (values, formikHelpers) => {
        await submitForm(values, formikHelpers);
      }}
    >
      {(formik) => (
        <ResizableDrawer
          isOpen={isOpen}
          placement="right"
          onClose={handleClose}
          size="lg"
          header={headerContent}
          footer={
            <>
              <Button
                variant="outline"
                mr={3}
                onClick={handleClose}
                width="50%"
                colorScheme="gray"
              >
                Cancel
              </Button>
              <Button
                colorScheme="primary"
                width="50%"
                type="submit"
                form={componentId}
                isLoading={formik.isSubmitting}
              >
                Save
              </Button>
            </>
          }
          autosaveId="clear-lower-ranked-drawer-width"
        >
          <Form id={componentId} noValidate>
            <Flex gap="1.5rem" pb="1.5rem" direction="column">
              {generalErrors.map((error) => (
                <Text key={error} fontSize="sm" color="red.500">
                  {error}
                </Text>
              ))}

              <Flex direction="column" gap="0.625rem">
                <FormLabel p="0" m="0" color="gray.700">
                  Rule name
                </FormLabel>
                <InputControl
                  name="name"
                  isRequired
                  inputProps={{
                    placeholder:
                      "E.g.: Clear lower ranked waitlists and offers",
                    autoComplete: "off",
                    ...PASSWORD_MANAGER_IGNORE,
                    onChange: (event) => {
                      const name = event.target.value;
                      void formik.setFieldValue("name", name);
                      if (!formik.values.userGeneratedKey) {
                        void formik.setFieldValue("key", generateKey(name));
                      }
                    },
                  }}
                />
              </Flex>

              <Flex direction="column" gap="0.625rem">
                <FormLabel p="0" m="0" color="gray.700">
                  Rule key
                </FormLabel>
                <InputControl
                  name="key"
                  isRequired
                  inputProps={{
                    placeholder: "E.g.: clear-lower-ranked",
                    autoComplete: "off",
                    ...PASSWORD_MANAGER_IGNORE,
                    onChange: (event) => {
                      const key = event.target.value;
                      void formik.setFieldValue("key", key);
                      void formik.setFieldValue(
                        "userGeneratedKey",
                        key.trim().length > 0
                      );
                    },
                  }}
                />
                {keyError && (
                  <Text fontSize="sm" color="red.500">
                    {keyError}
                  </Text>
                )}
              </Flex>

              <Flex direction="column" gap="0.625rem">
                <FormLabel p="0" m="0" color="gray.700">
                  Rule description (optional)
                </FormLabel>
                <InputControl
                  name="description"
                  inputProps={{
                    placeholder:
                      "E.g.: When an offer is accepted, automatically clear lower ranked waitlists and offers",
                    autoComplete: "off",
                    ...PASSWORD_MANAGER_IGNORE,
                  }}
                />
              </Flex>

              <Flex
                direction="column"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="md"
              >
                <Flex direction="column" gap="0.5rem" p="1rem">
                  <Heading as="h4" size="sm">
                    What would you like to do?
                  </Heading>
                  <Flex direction="column">
                    <Text fontWeight="semibold" fontSize="sm">
                      Clear lower ranked waitlists and offers
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      When an offer is accepted by family or staff,
                      automatically clear waitlists and offers at schools ranked
                      below the accepted school.
                    </Text>
                  </Flex>
                </Flex>

                <Flex
                  direction="column"
                  gap="1rem"
                  p="1rem"
                  borderTop="1px solid"
                  borderTopColor="gray.200"
                >
                  <Heading as="h4" size="sm">
                    What should happen when an offer is accepted?
                  </Heading>

                  <Flex direction="column" gap="0.25rem">
                    <Checkbox
                      colorScheme="primary"
                      isChecked={formik.values.remove_lower_ranked_waitlists}
                      onChange={(e) =>
                        void formik.setFieldValue(
                          "remove_lower_ranked_waitlists",
                          e.target.checked
                        )
                      }
                    >
                      <Text fontWeight="semibold" fontSize="sm">
                        Clear lower ranked waitlists
                      </Text>
                    </Checkbox>
                    <Text fontSize="sm" color="gray.600" pl="1.75rem">
                      Set the sub-status for lower ranked waitlisted schools to{" "}
                      <Text as="span" fontWeight="semibold">
                        Withdrawn
                      </Text>{" "}
                      (family-initiated) or{" "}
                      <Text as="span" fontWeight="semibold">
                        Removed
                      </Text>{" "}
                      (staff-initiated).
                    </Text>
                  </Flex>

                  <Flex direction="column" gap="0.25rem">
                    <Checkbox
                      colorScheme="primary"
                      isChecked={formik.values.revoke_lower_ranked_offers}
                      onChange={(e) =>
                        void formik.setFieldValue(
                          "revoke_lower_ranked_offers",
                          e.target.checked
                        )
                      }
                    >
                      <Text fontWeight="semibold" fontSize="sm">
                        Clear lower ranked offers
                      </Text>
                    </Checkbox>
                    <Text fontSize="sm" color="gray.600" pl="1.75rem">
                      Set the sub-status for lower ranked offered schools to{" "}
                      <Text as="span" fontWeight="semibold">
                        Declined
                      </Text>{" "}
                      (family-initiated) or{" "}
                      <Text as="span" fontWeight="semibold">
                        Revoked
                      </Text>{" "}
                      (staff-initiated).
                    </Text>
                  </Flex>
                </Flex>

                <Flex
                  direction="column"
                  gap="0.5rem"
                  p="1rem"
                  borderTop="1px solid"
                  borderTopColor="gray.200"
                >
                  <Tag
                    bg="primary.500"
                    color="white"
                    alignSelf="flex-start"
                    size="sm"
                  >
                    <TagLeftIcon as={RiLightbulbLine} />
                    <TagLabel>Tip</TagLabel>
                  </Tag>
                  <Text fontWeight="400">
                    This rule will not affect the accepted school or any school
                    choices ranked above it.
                  </Text>
                </Flex>
              </Flex>
            </Flex>
          </Form>
        </ResizableDrawer>
      )}
    </Formik>
  );
};
